'use client'

import { useMemo, useRef, useState } from 'react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { format } from 'date-fns'
import { AnimatePresence, motion } from 'framer-motion'
import {
  ArrowLeft,
  CalendarIcon,
  MessageCircle,
  Minus,
  Plus,
  Sun,
  Sunset,
} from 'lucide-react'
import { cn } from '@/lib/utils'
import { buildWhatsAppUrl } from '@/lib/links'
import { captureAnalyticsEvent } from '@/lib/analytics'
import { submitPreBookForm } from '@/lib/supabase/form-actions'
import { getCanonicalEditorial } from '@/lib/canonical-experiences'
import { getHomepageExperienceBySlug } from '@/lib/homepage-experiences'
import { preBookFormSchema, type PreBookFormData } from '@/lib/types/forms'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog'
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form'
import { Calendar } from '@/components/ui/calendar'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import { toast } from 'sonner'
import Link from 'next/link'

type SessionType = PreBookFormData['sessionType']
type PreferredSession = PreBookFormData['preferredSession']
type TrackedField =
  | 'name'
  | 'email'
  | 'phone'
  | 'preferredDate'
  | 'totalPersons'
  | 'preferredSession'

interface PreBookDialogProps {
  trigger: React.ReactNode
  experienceName: string
  experienceSlug?: string
  location?: string
}

const fieldClassName =
  'h-11 w-full rounded-xl border border-input bg-background px-3.5 text-sm text-foreground placeholder:text-muted-foreground/60 outline-none transition focus-visible:border-brand focus-visible:ring-2 focus-visible:ring-brand/20'
const labelClassName = 'mb-1.5 block text-[0.58rem] font-semibold uppercase tracking-[0.16em] text-muted-foreground'
const messageClassName = 'text-destructive'

function SharedBookingArt({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 72 56" className={className} aria-hidden="true" fill="none">
      <ellipse cx="36" cy="48" rx="24" ry="5" stroke="currentColor" strokeWidth="1.4" opacity="0.3" />
      <path d="M16 38h40" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" opacity="0.4" />
      <circle cx="20" cy="20" r="5.5" stroke="currentColor" strokeWidth="1.5" />
      <path d="M12 36c1.5-8 5-12 8-12s6.5 4 8 12" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
      <circle cx="36" cy="16" r="6" stroke="currentColor" strokeWidth="1.5" />
      <path d="M26 36c2-10 6.5-14 10-14s8 4 10 14" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
      <circle cx="52" cy="20" r="5.5" stroke="currentColor" strokeWidth="1.5" />
      <path d="M44 36c1.5-8 5-12 8-12s6.5 4 8 12" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
    </svg>
  )
}

function PrivateBookingArt({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 72 56" className={className} aria-hidden="true" fill="none">
      <ellipse cx="36" cy="48" rx="22" ry="5" stroke="currentColor" strokeWidth="1.4" opacity="0.3" />
      <path d="M18 38h36" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" opacity="0.4" />
      <circle cx="26" cy="18" r="6" stroke="currentColor" strokeWidth="1.5" />
      <path d="M16 36c2-10 6.5-14 10-14s8 4 10 14" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
      <circle cx="46" cy="18" r="6" stroke="currentColor" strokeWidth="1.5" />
      <path d="M36 36c2-10 6.5-14 10-14s8 4 10 14" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
    </svg>
  )
}

function BookingTypeCard({
  type,
  selected,
  priceLabel,
  onSelect,
}: {
  type: SessionType
  selected: boolean
  priceLabel: string
  onSelect: () => void
}) {
  const isShared = type === 'shared'

  return (
    <button
      type="button"
      onClick={onSelect}
      aria-pressed={selected}
      className={cn(
        'grid w-full grid-cols-[auto_minmax(0,1fr)] items-center gap-x-3 gap-y-2 rounded-2xl border px-3.5 py-3 text-left transition focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand/30 sm:grid-cols-[auto_minmax(0,1fr)_auto] sm:gap-4 sm:px-4 sm:py-3.5',
        selected
          ? isShared
            ? 'border-brand/45 bg-accent'
            : 'border-[#2e705b]/40 bg-secondary'
          : 'border-border bg-background hover:border-foreground/20 hover:bg-secondary/55',
      )}
    >
      <span
        className={cn(
          'grid size-4 shrink-0 place-items-center rounded-full border-2',
          selected
            ? isShared
              ? 'border-brand bg-brand'
              : 'border-[#2e705b] bg-[#2e705b]'
            : 'border-foreground/25 bg-transparent',
        )}
        aria-hidden="true"
      >
        {selected && <span className="size-1 rounded-full bg-white" />}
      </span>

      <div className="min-w-0 self-center">
        <p className={cn('text-[0.58rem] font-semibold uppercase tracking-[0.14em]', isShared ? 'text-accent-foreground' : 'text-[#2e705b]')}>
          {isShared ? 'Shared' : 'Private'}
        </p>
        <p className="mt-1 text-[0.9rem] font-semibold leading-tight text-foreground sm:truncate sm:text-[0.95rem] sm:leading-none">
          {isShared ? 'Shared with others' : 'Private for your group'}
        </p>
        <p className="mt-1.5 text-xs leading-none text-muted-foreground sm:truncate">
          {isShared ? 'Join a small group' : 'Just your group'}
        </p>
      </div>

      <div className="col-start-2 flex min-w-0 items-center justify-between gap-2 sm:col-start-auto sm:flex-col sm:items-end sm:gap-1.5">
        <div className={cn('hidden sm:block', isShared ? 'text-accent-foreground' : 'text-[#2e705b]')}>
          {isShared ? (
            <SharedBookingArt className="h-9 w-14 sm:h-10 sm:w-16" />
          ) : (
            <PrivateBookingArt className="h-9 w-14 sm:h-10 sm:w-16" />
          )}
        </div>
        <span
          className={cn(
            'max-w-full rounded-full px-2 py-1 text-[0.6rem] font-medium leading-tight sm:py-0.5 sm:text-[0.62rem] sm:leading-none',
            isShared ? 'bg-brand-soft text-accent-foreground' : 'bg-green-soft text-[#2e705b]',
          )}
        >
          {priceLabel}
        </span>
      </div>
    </button>
  )
}

function StepDots({ step }: { step: 'session' | 'details' }) {
  return (
    <div className="flex items-center gap-1.5" aria-hidden="true">
      <span className={cn('h-1 w-5 rounded-full transition', step === 'session' ? 'bg-brand' : 'bg-foreground/15')} />
      <span className={cn('h-1 w-5 rounded-full transition', step === 'details' ? 'bg-brand' : 'bg-foreground/15')} />
    </div>
  )
}

function buildPreBookWhatsAppMessage(
  experienceName: string,
  location: string | undefined,
  data: PreBookFormData,
) {
  const sessionLabel = data.sessionType === 'private' ? 'Private' : 'Shared'
  const preferredSessionLabel = data.preferredSession === 'morning' ? 'Morning' : 'Afternoon'
  const contactLines = [
    data.email?.trim() ? `Email: ${data.email.trim()}` : null,
    data.phone?.trim() ? `Phone: ${data.phone.trim()}` : null,
  ].filter(Boolean)

  return [
    `Hi Oorva! I'd like to pre-book an experience.`,
    '',
    `Experience: ${experienceName}`,
    location ? `Location: ${location}` : null,
    `Session type: ${sessionLabel}`,
    `Preferred date: ${format(data.preferredDate, 'PPP')}`,
    `Preferred session: ${preferredSessionLabel}`,
    `Total persons: ${data.totalPersons}`,
    '',
    `Name: ${data.name}`,
    ...contactLines,
    '',
    `Please get back to me to confirm availability.`,
  ]
    .filter((line): line is string => line !== null)
    .join('\n')
}

function isInsidePopover(target: EventTarget | null) {
  if (!(target instanceof Element)) return false
  return Boolean(
    target.closest('[data-slot="popover-content"]') ||
      target.closest('[data-radix-popper-content-wrapper]') ||
      target.closest('[data-slot="calendar"]'),
  )
}

export function PreBookDialog({
  trigger,
  experienceName,
  experienceSlug,
  location,
}: PreBookDialogProps) {
  const [open, setOpen] = useState(false)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [step, setStep] = useState<'session' | 'details'>('session')
  const [sessionType, setSessionType] = useState<SessionType | null>(null)
  const [datePickerOpen, setDatePickerOpen] = useState(false)
  const trackedFieldsRef = useRef<Set<TrackedField>>(new Set())
  const didSubmitRef = useRef(false)

  const analyticsBase = useMemo(
    () => ({
      experience_name: experienceName,
      experience_slug: experienceSlug,
      experience_location: location,
    }),
    [experienceName, experienceSlug, location],
  )

  const withSource = (properties: Record<string, string | number | boolean | null | undefined> = {}) => ({
    ...analyticsBase,
    source_path: typeof window !== 'undefined' ? window.location.pathname : undefined,
    ...properties,
  })

  const experienceDetails = useMemo(
    () => (experienceSlug ? getHomepageExperienceBySlug(experienceSlug) : undefined),
    [experienceSlug],
  )

  const editorialDetails = useMemo(
    () => (experienceSlug ? getCanonicalEditorial(experienceSlug) : undefined),
    [experienceSlug],
  )

  const priceLabel = experienceDetails?.timeSlot
    ? `Typical slot: ${experienceDetails.timeSlot}`
    : (editorialDetails?.price ?? 'Pricing confirmed on WhatsApp')

  const form = useForm<PreBookFormData>({
    resolver: zodResolver(preBookFormSchema),
    defaultValues: {
      name: '',
      email: '',
      phone: '',
      totalPersons: '2',
    },
    mode: 'onBlur',
  })

  const trackFieldCompleted = (field: TrackedField) => {
    if (trackedFieldsRef.current.has(field)) return
    trackedFieldsRef.current.add(field)
    captureAnalyticsEvent('pre_book_field_completed', withSource({
      field,
      session_type: sessionType,
      step: 'details',
      fields_completed: trackedFieldsRef.current.size,
    }))
  }

  const resetFlow = () => {
    setStep('session')
    setSessionType(null)
    setDatePickerOpen(false)
    trackedFieldsRef.current = new Set()
    didSubmitRef.current = false
    form.reset({
      name: '',
      email: '',
      phone: '',
      totalPersons: '2',
      preferredDate: undefined,
      preferredSession: undefined,
      sessionType: undefined,
    })
  }

  const handleOpenChange = (nextOpen: boolean) => {
    if (nextOpen && !open) {
      didSubmitRef.current = false
      captureAnalyticsEvent('pre_book_opened', withSource())
      captureAnalyticsEvent('inquiry_opened', withSource({ inquiry_type: 'pre_book' }))
    }
    if (!nextOpen && open) {
      if (!didSubmitRef.current) {
        captureAnalyticsEvent('pre_book_closed', withSource({
          session_type: sessionType,
          step,
          fields_completed: trackedFieldsRef.current.size,
        }))
      }
      resetFlow()
    }
    setOpen(nextOpen)
  }

  const handleSessionSelect = (type: SessionType) => {
    setSessionType(type)
    form.setValue('sessionType', type, { shouldValidate: true })
    captureAnalyticsEvent('pre_book_session_selected', withSource({ session_type: type }))
    captureAnalyticsEvent('pre_book_step_continued', withSource({
      session_type: type,
      from_step: 'session',
      to_step: 'details',
    }))
    setStep('details')
  }

  const onSubmit = async (data: PreBookFormData) => {
    didSubmitRef.current = true
    const sourcePath = typeof window !== 'undefined' ? window.location.pathname : undefined

    captureAnalyticsEvent('pre_book_submitted', withSource({
      session_type: data.sessionType,
      preferred_session: data.preferredSession,
      total_persons: data.totalPersons,
      fields_completed: trackedFieldsRef.current.size,
      has_email: Boolean(data.email?.trim()),
      has_phone: Boolean(data.phone?.trim()),
    }))
    captureAnalyticsEvent('inquiry_submitted', withSource({
      inquiry_type: 'pre_book',
      session_type: data.sessionType,
    }))

    setIsSubmitting(true)
    try {
      await submitPreBookForm(data, {
        experienceName,
        experienceSlug,
        location,
        sourcePath,
      })
      captureAnalyticsEvent('inquiry_submission_succeeded', withSource({ inquiry_type: 'pre_book' }))
    } catch {
      captureAnalyticsEvent('inquiry_submission_failed', withSource({ inquiry_type: 'pre_book' }))
    } finally {
      setIsSubmitting(false)
    }

    captureAnalyticsEvent('booking_intent_clicked', withSource({
      channel: 'whatsapp',
      session_type: data.sessionType,
    }))

    window.open(
      buildWhatsAppUrl(buildPreBookWhatsAppMessage(experienceName, location, data)),
      '_blank',
      'noopener,noreferrer',
    )
    toast.success('Opening WhatsApp', {
      description: 'Your pre-book details are drafted — send the message to confirm with us.',
    })
    setOpen(false)
    resetFlow()
  }

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogTrigger asChild>{trigger}</DialogTrigger>
      <DialogContent
        overlayClassName="bg-primary/55 backdrop-blur-sm"
        className={cn(
          'oorva-site-theme flex max-h-[min(90dvh,720px)] flex-col gap-0 overflow-hidden rounded-[1.5rem] border-border bg-card p-0 text-foreground shadow-[0_28px_80px_rgba(16,46,38,0.24)] sm:max-w-md',
          '[&_[data-slot=dialog-close]]:grid [&_[data-slot=dialog-close]]:size-10 [&_[data-slot=dialog-close]]:place-items-center [&_[data-slot=dialog-close]]:rounded-full',
          '[&_[data-slot=dialog-close]]:text-muted-foreground [&_[data-slot=dialog-close]]:hover:bg-secondary [&_[data-slot=dialog-close]]:hover:text-foreground',
          '[&_[data-slot=dialog-close]]:ring-offset-card [&_[data-slot=dialog-close]]:focus:ring-brand',
        )}
        onPointerDownOutside={(event) => {
          if (isInsidePopover(event.target) || datePickerOpen) {
            event.preventDefault()
          }
        }}
        onInteractOutside={(event) => {
          if (isInsidePopover(event.target) || datePickerOpen) {
            event.preventDefault()
          }
        }}
        onFocusOutside={(event) => {
          if (isInsidePopover(event.target) || datePickerOpen) {
            event.preventDefault()
          }
        }}
      >
        <div className="shrink-0 border-b border-border px-5 pb-4 pt-5">
          <DialogHeader className="gap-2 text-left">
            <div className="flex items-center justify-between gap-3 pr-10">
              <p className="text-[0.58rem] font-semibold uppercase tracking-[0.22em] text-accent-foreground">
                Step {step === 'session' ? '1' : '2'} of 2
              </p>
              <StepDots step={step} />
            </div>
            <DialogTitle className="font-sans text-[1.45rem] font-semibold leading-tight tracking-[-0.02em] text-foreground">
              {step === 'session' ? 'Choose your booking type' : 'Your visit details'}
            </DialogTitle>
            <DialogDescription className="text-sm leading-5 text-muted-foreground">
              {step === 'session'
                ? 'Pick what suits you best.'
                : `We’ll confirm ${experienceName} on WhatsApp.`}
            </DialogDescription>
          </DialogHeader>
        </div>

        <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain px-5 py-4">
          <AnimatePresence mode="wait" initial={false}>
            {step === 'session' ? (
              <motion.div
                key="session"
                initial={{ opacity: 0, y: 8 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -6 }}
                transition={{ duration: 0.18, ease: [0.22, 1, 0.36, 1] }}
                className="space-y-2.5"
              >
                <BookingTypeCard
                  type="shared"
                  selected={sessionType === 'shared'}
                  priceLabel={priceLabel}
                  onSelect={() => handleSessionSelect('shared')}
                />
                <BookingTypeCard
                  type="private"
                  selected={sessionType === 'private'}
                  priceLabel={priceLabel}
                  onSelect={() => handleSessionSelect('private')}
                />
              </motion.div>
            ) : (
              <motion.div
                key="details"
                initial={{ opacity: 0, y: 8 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -6 }}
                transition={{ duration: 0.18, ease: [0.22, 1, 0.36, 1] }}
              >
                <Form {...form}>
                  <form id="pre-book-details-form" onSubmit={form.handleSubmit(onSubmit)} className="space-y-5">
                    <button
                      type="button"
                      onClick={() => setStep('session')}
                      className="inline-flex items-center gap-1.5 text-xs text-muted-foreground transition hover:text-foreground"
                    >
                      <ArrowLeft className="size-3.5" />
                      {sessionType === 'private' ? 'Private' : 'Shared'}
                    </button>

                    <section className="space-y-3">
                      <p className="text-[0.58rem] font-semibold uppercase tracking-[0.16em] text-muted-foreground">Contact</p>
                      <FormField
                        control={form.control}
                        name="name"
                        render={({ field }) => (
                          <FormItem className="gap-0">
                            <FormLabel className={labelClassName}>Name *</FormLabel>
                            <FormControl>
                              <input
                                className={fieldClassName}
                                placeholder="Your full name"
                                {...field}
                                onBlur={(event) => {
                                  field.onBlur()
                                  if (event.target.value.trim()) trackFieldCompleted('name')
                                }}
                                onChange={(event) => {
                                  field.onChange(event)
                                  if (event.target.value.trim()) trackFieldCompleted('name')
                                }}
                              />
                            </FormControl>
                            <FormMessage className={cn(messageClassName, 'mt-1.5')} />
                          </FormItem>
                        )}
                      />

                      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 sm:gap-3">
                        <FormField
                          control={form.control}
                          name="email"
                          render={({ field }) => (
                            <FormItem className="gap-0">
                              <FormLabel className={labelClassName}>Email</FormLabel>
                              <FormControl>
                                <input
                                  className={fieldClassName}
                                  type="email"
                                  placeholder="you@email.com"
                                  {...field}
                                  onBlur={(event) => {
                                    field.onBlur()
                                    if (event.target.value.trim()) trackFieldCompleted('email')
                                  }}
                                  onChange={(event) => {
                                    field.onChange(event)
                                    if (event.target.value.trim()) trackFieldCompleted('email')
                                  }}
                                />
                              </FormControl>
                              <FormMessage className={cn(messageClassName, 'mt-1.5')} />
                            </FormItem>
                          )}
                        />
                        <FormField
                          control={form.control}
                          name="phone"
                          render={({ field }) => (
                            <FormItem className="gap-0">
                              <FormLabel className={labelClassName}>Phone</FormLabel>
                              <FormControl>
                                <input
                                  className={fieldClassName}
                                  type="tel"
                                  placeholder="+91 …"
                                  {...field}
                                  onBlur={(event) => {
                                    field.onBlur()
                                    if (event.target.value.trim()) trackFieldCompleted('phone')
                                  }}
                                  onChange={(event) => {
                                    field.onChange(event)
                                    if (event.target.value.trim()) trackFieldCompleted('phone')
                                  }}
                                />
                              </FormControl>
                              <FormMessage className={cn(messageClassName, 'mt-1.5')} />
                            </FormItem>
                          )}
                        />
                      </div>
                      <p className="text-xs text-muted-foreground">Provide at least one contact method.</p>
                    </section>

                    <section className="space-y-3">
                      <p className="text-[0.58rem] font-semibold uppercase tracking-[0.16em] text-muted-foreground">Visit</p>
                      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 sm:gap-3">
                        <FormField
                          control={form.control}
                          name="preferredDate"
                          render={({ field }) => (
                            <FormItem className="flex flex-col gap-0">
                              <FormLabel className={labelClassName}>Preferred date *</FormLabel>
                              <Popover open={datePickerOpen} onOpenChange={setDatePickerOpen}>
                                <PopoverTrigger asChild>
                                  <button
                                    type="button"
                                    className={cn(
                                      fieldClassName,
                                      'inline-flex items-center justify-between text-left',
                                      !field.value && 'text-muted-foreground/60',
                                    )}
                                  >
                                    {field.value ? format(field.value, 'PPP') : 'Pick a date'}
                                    <CalendarIcon className="size-4 opacity-45" />
                                  </button>
                                </PopoverTrigger>
                                <PopoverContent
                                  className="oorva-site-theme z-[70] w-auto border-border bg-card p-0 text-foreground shadow-2xl"
                                  align="start"
                                  onOpenAutoFocus={(event) => event.preventDefault()}
                                  onCloseAutoFocus={(event) => event.preventDefault()}
                                >
                                  <Calendar
                                    mode="single"
                                    selected={field.value}
                                    onSelect={(date) => {
                                      field.onChange(date)
                                      if (date) {
                                        trackFieldCompleted('preferredDate')
                                        setDatePickerOpen(false)
                                      }
                                    }}
                                    disabled={(date) => date < new Date(new Date().setHours(0, 0, 0, 0))}
                                    initialFocus
                                    className="bg-card text-foreground"
                                  />
                                </PopoverContent>
                              </Popover>
                              <FormMessage className={cn(messageClassName, 'mt-1.5')} />
                            </FormItem>
                          )}
                        />

                        <FormField
                          control={form.control}
                          name="totalPersons"
                          render={({ field }) => {
                            const count = Math.min(20, Math.max(1, Number(field.value) || 2))
                            const setCount = (next: number) => {
                              const clamped = Math.min(20, Math.max(1, next))
                              field.onChange(String(clamped))
                              trackFieldCompleted('totalPersons')
                            }

                            return (
                              <FormItem className="gap-0">
                                <FormLabel className={labelClassName}>Total persons *</FormLabel>
                                <FormControl>
                                  <div className="inline-flex h-11 w-full items-center justify-between rounded-xl border border-input bg-background px-1.5">
                                    <button
                                      type="button"
                                      aria-label="Decrease guests"
                                      disabled={count <= 1}
                                      onClick={() => setCount(count - 1)}
                                      className="grid size-8 place-items-center rounded-lg text-muted-foreground transition hover:bg-secondary hover:text-foreground disabled:cursor-not-allowed disabled:opacity-30"
                                    >
                                      <Minus className="size-4" />
                                    </button>
                                    <span className="min-w-8 text-center text-sm font-semibold tabular-nums text-foreground">
                                      {count}
                                    </span>
                                    <button
                                      type="button"
                                      aria-label="Increase guests"
                                      disabled={count >= 20}
                                      onClick={() => setCount(count + 1)}
                                      className="grid size-8 place-items-center rounded-lg text-muted-foreground transition hover:bg-secondary hover:text-foreground disabled:cursor-not-allowed disabled:opacity-30"
                                    >
                                      <Plus className="size-4" />
                                    </button>
                                  </div>
                                </FormControl>
                                <FormMessage className={cn(messageClassName, 'mt-1.5')} />
                              </FormItem>
                            )
                          }}
                        />
                      </div>

                      <FormField
                        control={form.control}
                        name="preferredSession"
                        render={({ field }) => (
                          <FormItem className="gap-0">
                            <FormLabel className={labelClassName}>Preferred session *</FormLabel>
                            <FormControl>
                              <div className="grid grid-cols-2 gap-2">
                                {(
                                  [
                                    { id: 'morning' as PreferredSession, label: 'Morning', icon: Sun },
                                    { id: 'afternoon' as PreferredSession, label: 'Afternoon', icon: Sunset },
                                  ] as const
                                ).map(({ id, label, icon: Icon }) => {
                                  const selected = field.value === id
                                  return (
                                    <button
                                      key={id}
                                      type="button"
                                      onClick={() => {
                                        field.onChange(id)
                                        trackFieldCompleted('preferredSession')
                                      }}
                                      className={cn(
                                        'inline-flex h-11 items-center justify-center gap-2 rounded-xl border text-sm font-medium transition',
                                        selected
                                          ? 'border-brand bg-accent text-foreground'
                                          : 'border-input bg-background text-muted-foreground hover:border-foreground/20 hover:text-foreground',
                                      )}
                                    >
                                      <Icon className={cn('size-4', selected ? 'text-brand' : 'text-muted-foreground')} />
                                      {label}
                                    </button>
                                  )
                                })}
                              </div>
                            </FormControl>
                            <FormMessage className={cn(messageClassName, 'mt-1.5')} />
                          </FormItem>
                        )}
                      />
                    </section>
                  </form>
                </Form>
              </motion.div>
            )}
          </AnimatePresence>
        </div>

        {step === 'details' && (
          <div className="shrink-0 border-t border-border bg-background px-5 py-3.5">
            <button
              type="submit"
              form="pre-book-details-form"
              disabled={isSubmitting}
              className="inline-flex h-11 w-full items-center justify-center gap-2 rounded-full bg-brand px-5 text-sm font-semibold text-white transition hover:bg-brand-hover disabled:cursor-not-allowed disabled:opacity-70"
            >
              <MessageCircle className="size-4" />
              {isSubmitting ? 'Saving…' : 'Send request on WhatsApp'}
            </button>
            <p className="mt-2 text-center text-xs text-muted-foreground">No seat is held until availability and payment are confirmed. <Link href="/docs/guest-policy" className="underline">Cancellation terms vary by experience.</Link></p>
          </div>
        )}
      </DialogContent>
    </Dialog>
  )
}
